import type { ContractorTranslation } from './en';

export const esES: ContractorTranslation = {
  common: {
    cancel: 'Cancelar',
    save: 'Guardar',
    retry: 'Reintentar',
    download: 'Descargar',
    continue: 'Continuar',
    yes: 'Sí',
    no: 'No',
    genericError: 'Algo ha salido mal. Inténtalo de nuevo.',
    networkError: 'No se ha podido conectar con DubForge. Comprueba tu conexión a internet.',
  },
  app: {
    tagline: 'Encargos de doblaje, archivos y revisiones en un solo sitio.',
    startupFailed: 'DubForge no ha podido iniciarse',
    notFoundTitle: 'Página no encontrada',
    notFoundBody: 'Esa página no existe o no tienes acceso a ella.',
    goHome: 'Ir al inicio',
    crashTitle: 'Algo ha salido mal',
    crashBody: 'Prueba a recargar la página.',
    reload: 'Recargar',
  },
  language: {
    label: 'Idioma',
    change: 'Cambiar idioma',
  },
  welcome: {
    title: 'Te damos la bienvenida a DubForge',
    body: 'Elige tu idioma. DubForge y tus notificaciones de Discord lo usarán.',
    changeLater: 'Puedes cambiarlo cuando quieras desde el menú de idioma, arriba a la derecha.',
  },
  nav: {
    myAssignments: 'Mis encargos',
    openMenu: 'Abrir menú',
    account: 'Cuenta',
    notificationEmail: 'Correo para notificaciones',
    signOut: 'Cerrar sesión',
    roleAdmin: 'Administración',
    roleContractor: 'Talento de doblaje',
    lightMode: 'Modo claro',
    darkMode: 'Modo oscuro',
  },
  login: {
    signIn: 'Iniciar sesión con Discord',
    onlyInvited: 'Solo pueden iniciar sesión las cuentas de Discord que haya añadido el propietario del canal.',
    signingIn: 'Iniciando sesión…',
    backToSignIn: 'Volver al inicio de sesión',
    failed: 'No se ha podido iniciar sesión. Inténtalo de nuevo.',
  },
  authErrors: {
    not_invited:
      'Esta cuenta de Discord no se ha añadido a DubForge. Pide al propietario del canal que te añada.',
    access_off: 'Se ha desactivado tu acceso a DubForge.',
    discord_only: 'Solo puedes iniciar sesión con Discord.',
  },
  errors: {
    not_signed_in: 'Tu sesión ha caducado. Vuelve a iniciar sesión.',
    access_off: 'Se ha desactivado tu acceso a DubForge.',
    admins_only: 'No tienes permiso para hacer eso.',
    not_found: 'Ese elemento no existe o ya no está disponible.',
    invalid_request: 'Hay algo en la solicitud que no es válido. Revísalo e inténtalo de nuevo.',
    conflict: 'Eso ya existe.',
    already_approved: 'Este encargo ya se ha aprobado, así que no hace falta subir nada más.',
    not_audio: 'Solo se pueden subir archivos WAV o MP3. Sube solo tu pista de voz.',
    upload_incomplete: 'La subida no ha terminado. Inténtalo de nuevo.',
    discord_unreachable: 'No se ha podido conectar con Discord. Inténtalo más tarde.',
    server_error: 'Algo ha salido mal por nuestra parte. Inténtalo de nuevo.',
  },
  notifications: {
    title: 'Notificaciones',
    openWithCount: 'Notificaciones ({{count}} sin leer)',
    markAllRead: 'Marcar todo como leído',
    empty: 'Todavía no hay nada.',
  },
  emailSettings: {
    body: 'Te enviamos un correo cuando recibes un nuevo encargo y cuando se revisa tu trabajo.',
    label: 'Dirección de correo',
    fromDiscord: 'Se ha tomado de tu cuenta de Discord. Cámbialo si prefieres otra dirección.',
    saved: 'Se ha guardado la dirección de correo',
    invalid: 'Introduce una dirección de correo válida.',
    missing: 'Todavía no hay ninguna dirección de correo. Añade una para recibir notificaciones.',
  },
  status: {
    Assigned: 'Asignado',
    InProgress: 'En curso',
    Submitted: 'Pendiente de revisión',
    ChangesRequested: 'Cambios solicitados',
    Approved: 'Aprobado',
  },
  assetKind: {
    package: 'Paquete de doblaje (vídeo + voz + SFX)',
    video: 'Vídeo de referencia (720p)',
    sfx: 'Pista de SFX / música',
    voice: 'Pista de voz original',
    script: 'Guion',
    other: 'Otro',
  },
  translation: {
    machine: 'Traducción automática',
    showOriginal: 'Ver original',
    showTranslation: 'Ver traducción',
  },
  uploader: {
    progress: '{{percent}} % de {{size}}',
    queued: 'Pendientes de subir: {{count}}',
    keepOpen: 'No cierres esta pestaña hasta que termine.',
    cancelled: 'Subida cancelada',
    done: 'Se ha subido {{name}}',
    failed: 'Error al subir: {{error}}',
  },
  my: {
    greeting: 'Hola, {{name}}',
    subtitle: 'Tus encargos de doblaje.',
    emptyTitle: 'Todavía no tienes encargos',
    emptyBody: 'Cuando te asignen un vídeo, recibirás un mensaje en Discord y aparecerá aquí.',
    todo: 'Pendientes',
    waiting: 'Pendientes de revisión',
    done: 'Terminados',
    updated: 'Actualizado {{when}}',
  },
  assignment: {
    back: 'Mis encargos',
    dubIn: 'Doblaje: {{language}}',
    paid: 'Pagado',
    next: {
      Assigned: 'Descarga los archivos de origen, graba tu voz y después sube aquí tu pista de voz.',
      InProgress: 'Cuando tengas lista tu pista de voz, súbela aquí abajo.',
      Submitted: 'Tu doblaje se ha enviado a revisión. Recibirás un mensaje en Discord cuando lo revisen.',
      ChangesRequested: 'Se han solicitado cambios. Lee los comentarios de abajo y después sube una versión nueva.',
      Approved: 'Aprobado. ¡Gracias!',
    },
    notes: 'Notas',
    sourceFiles: 'Archivos de origen',
    noFiles: 'Todavía no se ha subido ningún archivo.',
    fileInfo: '{{kind}} · {{size}}',
    packageHint: 'Un único .mp4 con el vídeo y dos pistas de audio: la pista 1 son las líneas de voz originales y la pista 2 es el SFX.',
    uploadTitle: 'Sube tu pista de voz',
    voiceOnlyTitle: 'Solo tu voz',
    voiceOnlyBody:
      'Sube solo tu voz grabada: sin música, sin efectos de sonido y sin nada del audio original. Asegúrate de que dure lo mismo que el original.',
    voiceOnlyConfirm: 'Este archivo contiene solo líneas de voz.',
    dropLabel: 'Sube aquí tu pista de voz',
    dropHint: 'Solo WAV o MP3.',
    noteForReviewer: 'Nota para quien revise (opcional)',
    lengthTitle: 'La duración no coincide',
    lengthBody:
      'Tu archivo dura {{dub}}, pero la pista de SFX dura {{sfx}}. Si tu pista empieza en otro punto o está cortada, no quedará sincronizada con el vídeo. ¿Quieres subirla igualmente?',
    uploadAnyway: 'Subir igualmente',
    chooseAnother: 'Elegir otro archivo',
    versions: 'Versiones subidas',
    nothingUploaded: 'Todavía no se ha subido nada.',
    version: 'Versión {{version}}',
    reviewApproved: 'Aprobada',
    reviewChanges: 'Cambios solicitados',
    reviewPending: 'Pendiente de revisión',
    yourNote: 'Tu nota:',
    feedback: 'Comentarios',
    listen: 'Escuchar',
    uploadedOn: '{{date}} · {{size}}',
    length: 'Duración: {{duration}}',
  },
};
